import { BasicTimelineEventData } from 'components/Timeline';
import { getTrans } from 'modules/i18n';
import { GeneralStatus } from 'modules/status-badge';
import { isEffectiveValueVisible } from 'modules/week-details';
import {
  DateFormat,
  DEFAULT_TIMEZONE,
  formatDateWithTimezone,
  getHoursAndMinutesLabel,
  getShortDateLabelWithTimezone
} from 'utils/date';
import { Break, ShiftWorkLocation } from './types';

// "streetLine, postalCode city" from whatever address parts are present.
export const getWorkLocationAddressLine = (location: ShiftWorkLocation) =>
  [
    location.streetLine,
    [location.postalCode, location.city].filter(Boolean).join(' ')
  ]
    .filter(Boolean)
    .join(', ');

// Whether there is anything to display — a location may carry only a refId,
// in which case the location card should not render at all.
export const hasWorkLocationContent = (
  location: ShiftWorkLocation | undefined
): location is ShiftWorkLocation =>
  !!location &&
  [location.name, location.streetLine, location.postalCode, location.city].some(
    Boolean
  );

export const getShortDateWithWeekdayLabelWithTimezone = (
  dateString: string,
  timezone: string
) => {
  const weekdayLabel = formatDateWithTimezone(
    dateString,
    DateFormat.SHORT_WEEKDAY,
    timezone
  );

  const dateLabel = getShortDateLabelWithTimezone(dateString, DEFAULT_TIMEZONE);
  return `${weekdayLabel}, ${dateLabel}`;
};

export const getFullDateTimeLabelWithTimezone = (
  dateString: string,
  timezone: string
) => {
  const timeLabel = formatDateWithTimezone(
    dateString,
    DateFormat.TIME,
    timezone
  );

  const dateLabel = getShortDateWithWeekdayLabelWithTimezone(
    dateString,
    timezone
  );
  return `${timeLabel} | ${dateLabel}`;
};

export const getBreakTimeLabelWithTimezone = (
  start: string,
  end: string,
  timezone: string
) => {
  const startTime = formatDateWithTimezone(start, DateFormat.TIME, timezone);
  const endTime = formatDateWithTimezone(end, DateFormat.TIME, timezone);
  return `${startTime} - ${endTime} | ${getShortDateWithWeekdayLabelWithTimezone(
    start,
    timezone
  )}`;
};

export const getEffectiveValueLabels = (
  effectiveShiftWorkMinutes: number | undefined,
  reportedShiftWorkMinutes: number | undefined,
  effectiveShiftBreakMinutes: number | undefined,
  reportedShiftBreakMinutes: number | undefined,
  shiftStatus: GeneralStatus | undefined
): {
  workedMinutes: string | undefined;
  breakMinutes: string | undefined;
  totalMinutes: string | undefined;
} => {
  const isShiftWorkVisible = isEffectiveValueVisible(
    effectiveShiftWorkMinutes,
    reportedShiftWorkMinutes,
    shiftStatus
  );
  const isShiftBreakVisible = isEffectiveValueVisible(
    effectiveShiftBreakMinutes,
    reportedShiftBreakMinutes,
    shiftStatus
  );

  return {
    workedMinutes: isShiftWorkVisible
      ? getHoursAndMinutesLabel(effectiveShiftWorkMinutes ?? 0)
      : undefined,
    breakMinutes: isShiftBreakVisible
      ? getHoursAndMinutesLabel(effectiveShiftBreakMinutes ?? 0)
      : undefined,
    totalMinutes:
      isShiftWorkVisible || isShiftBreakVisible
        ? getHoursAndMinutesLabel(
            (effectiveShiftWorkMinutes ?? reportedShiftWorkMinutes ?? 0) +
              (effectiveShiftBreakMinutes ?? reportedShiftBreakMinutes ?? 0)
          )
        : undefined
  };
};

export const getBreakTimes = ({
  reportedBreakStart,
  reportedBreakEnd,
  effectiveBreakStart,
  effectiveBreakEnd,
  shiftStatus
}: Break & { shiftStatus: GeneralStatus }) => {
  const isBreakStartVisible = isEffectiveValueVisible(
    effectiveBreakStart,
    reportedBreakStart,
    shiftStatus
  );
  const isBreakEndVisible = isEffectiveValueVisible(
    effectiveBreakEnd,
    reportedBreakEnd,
    shiftStatus
  );

  return {
    isBreakVisible: isBreakStartVisible || isBreakEndVisible,
    startBreak: effectiveBreakStart ?? reportedBreakStart,
    endBreak: effectiveBreakEnd ?? reportedBreakEnd
  };
};

export const getEffectiveBreakLabelWithTimezone = ({
  reportedBreakStart,
  reportedBreakEnd,
  effectiveBreakStart,
  effectiveBreakEnd,
  shiftStatus,
  timezone
}: Break & { shiftStatus: GeneralStatus; timezone: string }):
  | string
  | undefined => {
  const { isBreakVisible, startBreak, endBreak } = getBreakTimes({
    reportedBreakStart,
    reportedBreakEnd,
    effectiveBreakStart,
    effectiveBreakEnd,
    shiftStatus
  });

  if (isBreakVisible && startBreak && endBreak)
    return getBreakTimeLabelWithTimezone(startBreak, endBreak, timezone);

  return undefined;
};

export const getShiftBreaksDataWithTimezone = (
  shiftBreaks: Break[] | undefined,
  shiftStatus: GeneralStatus,
  timezone: string
): BasicTimelineEventData[] | undefined =>
  shiftBreaks?.map(breakItem => {
    const { reportedBreakStart, reportedBreakEnd } = breakItem;

    const reportedBreakLabel =
      reportedBreakStart &&
      reportedBreakEnd &&
      getBreakTimeLabelWithTimezone(
        reportedBreakStart,
        reportedBreakEnd,
        timezone
      );

    const effectiveBreakLabel = getEffectiveBreakLabelWithTimezone({
      ...breakItem,
      shiftStatus,
      timezone
    });

    return {
      label: getTrans('shiftDetailsPage.timeline.break'),
      value: reportedBreakLabel,
      newValue: effectiveBreakLabel
    };
  });
